import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLButtonElement} minusButton
 * @property {HTMLButtonElement} plusButton
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;
  /** @type {string | null} */
  originalMax = null;
  /** @type {(() => void) | null} */
  onRegionalChange = null;
  regionalSoldOut = false;

  connectedCallback() {
    super.connectedCallback();

    // Capture server-disabled state on first load
    const { minusButton, plusButton } = this.refs;

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }
    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.originalMax = this.refs.quantityInput.max || null;
    this.applyRegionalMax();
    this.onRegionalChange = () => this.applyRegionalMax();
    document.addEventListener('jv:region-changed', this.onRegionalChange);

    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.onRegionalChange) {
      document.removeEventListener('jv:region-changed', this.onRegionalChange);
    }
  }

  applyRegionalMax() {
    if (!window.JVRegionalStock) return;
    if (this.closest('[data-jv-montar-kit]')) return;

    const { quantityInput } = this.refs;
    const form = this.closest('product-form-component, cart-items-component, [data-jv-montar-kit], [data-jv-kit-builder]');
    const variantId = this.dataset.variantId || quantityInput?.dataset?.variantId;
    const cartQty = parseInt(quantityInput.getAttribute('data-cart-quantity') || '0', 10) || 0;
    const baseMax = this.originalMax ? parseInt(this.originalMax, 10) : null;

    if (!variantId) return;

    const effectiveMax = window.JVRegionalStock.getEffectiveMax({
      variantId,
      baseMax,
      cartQty,
      element: form || this,
    });

    if (effectiveMax == null) {
      this.regionalSoldOut = false;
      quantityInput.removeAttribute('data-jv-pdp-remaining-max');
      if (this.originalMax) {
        quantityInput.max = this.originalMax;
      } else {
        quantityInput.removeAttribute('max');
      }
      quantityInput.disabled = false;
      this.updateCartQuantity();
      return;
    }

    const cantAddMore = effectiveMax <= 0;
    const soldOutByRegion = cantAddMore && cartQty === 0;

    const totalCap = effectiveMax + cartQty;
    const minVal = parseInt(quantityInput.min, 10) || 1;

    this.regionalSoldOut = soldOutByRegion;

    if (soldOutByRegion) {
      quantityInput.removeAttribute('data-jv-pdp-remaining-max');
      quantityInput.max = '0';
      quantityInput.disabled = true;
      this.refs.plusButton.disabled = true;
      this.refs.minusButton.disabled = true;
      this.updateCartQuantity();
      return;
    }

    quantityInput.disabled = false;

    const isCartLine = quantityInput.dataset.cartLine != null && quantityInput.dataset.cartLine !== '';
    const isPdpProductForm = !isCartLine && !!this.closest('product-form-component');

    if (isCartLine) {
      quantityInput.removeAttribute('data-jv-pdp-remaining-max');
      const lineQty = parseInt(quantityInput.value, 10) || 0;
      const remaining = Math.max(0, effectiveMax);
      const lineMax = lineQty + remaining;
      quantityInput.max = String(Math.max(lineMax, minVal));
    } else if (isPdpProductForm) {
      quantityInput.setAttribute('data-jv-pdp-remaining-max', 'true');
      quantityInput.max = String(Math.max(effectiveMax, minVal));
    } else {
      quantityInput.removeAttribute('data-jv-pdp-remaining-max');
      quantityInput.max = String(Math.max(totalCap, minVal));
    }
    this.updateCartQuantity();
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.applyRegionalMax();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { quantityInput } = this.refs;
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const pdpRemaining = quantityInput.getAttribute('data-jv-pdp-remaining-max') === 'true';
    const wouldExceedMax =
      max !== null && (pdpRemaining ? quantityToAdd > max : cartQuantity + quantityToAdd > max);

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    this.originalMax = max || null;
    quantityInput.step = step;

    const newMin = parseInt(min) || 1;
    const newStep = parseInt(step) || 1;
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;
    return {
      min: parseInt(quantityInput.min) || 1,
      max: quantityInput.max ? parseInt(quantityInput.max) : null,
      step: parseInt(quantityInput.step) || 1,
      value: parseInt(quantityInput.value) || 0,
      cartQuantity: parseInt(quantityInput.getAttribute('data-cart-quantity') || '0'),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { quantityInput } = this.refs;
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    if (quantityInput.getAttribute('data-jv-pdp-remaining-max') === 'true') {
      return max;
    }
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Updates button states based on current value and limits
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (this.regionalSoldOut) {
      minusButton.disabled = true;
      plusButton.disabled = true;
      return;
    }

    // Only manage buttons that weren't server-disabled
    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }

  /**
   * Updates quantity by a given step
   * @param {number} stepMultiplier - Positive for increase, negative for decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, value + step * stepMultiplier)
    );

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity increase event.
   * @param {Event} event - The event.
   */
  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  /**
   * Handles the quantity decrease event.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Snap to bounds
    const quantity = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, parseInt(event.target.value) || 0)
    );

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes button states
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(
      effectiveMax ?? Infinity,
      Math.max(min, value)
    );

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
